"use client"

import * as React from "react"
import { createContext, useContext, useState, useCallback } from "react"
import { type DateRange } from "react-day-picker"
import { subDays, subMonths, subYears, startOfMonth, endOfMonth, startOfYear, endOfYear } from "date-fns"
import { useSavedViewsList, useCreateSavedView, useDeleteSavedView } from "@/data/hooks/api/useViews"
import type {
  SavedViewResponse,
  ViewFilters,
  ViewMetadataFilter,
  CreateSavedViewRequest,
  RelativeDateValue,
} from "@/data/types/views.types"
import type { ModelSelection } from "@/data/types/common.types"

// UI filter format used by FiltersSelector
export interface SavedViewFilter {
  id: string
  tagName: string
  tagNameLabel: string
  operation: string
  operationLabel: string
  value: string
}

// All filters state structure for syncing with pages
export interface AllFiltersState {
  dateRange: DateRange | undefined
  granularity: string
  dimension: string
  modelSelection: ModelSelection
  providers: string[] | undefined
  metadataFilters: SavedViewFilter[]
}

// Parameters for creating a saved view
export interface CreateViewParams {
  title: string
  description: string
  relativeDate: RelativeDateValue | null
  modelSelection: ModelSelection
  metadataFilters: SavedViewFilter[]
  granularity: string
  groupBy: string
}

// UI-facing saved view structure
export interface SavedView {
  id: string
  title: string
  description: string
  relativeDate: RelativeDateValue | null
  modelSelection: ModelSelection | null
  models: string[] // Legacy support
  metadataFilters: SavedViewFilter[]
  granularity: string | null
  groupBy: string | null
  createdBy: {
    id: string
    firstName: string
    lastName: string
    email: string
  }
  createdAt: string
  lastEditedAt: string
}

// Convert relative date value to actual DateRange
export function relativeDateToRange(relativeDate: RelativeDateValue | null | undefined): DateRange | undefined {
  if (!relativeDate) return undefined

  const now = new Date()

  switch (relativeDate) {
    case 'last_7_days':
      return { from: subDays(now, 6), to: now }
    case 'last_14_days':
      return { from: subDays(now, 13), to: now }
    case 'last_30_days':
      return { from: subDays(now, 29), to: now }
    case 'last_90_days':
      return { from: subDays(now, 89), to: now }
    case 'this_month':
      return { from: startOfMonth(now), to: now }
    case 'last_month':
      return { from: startOfMonth(subMonths(now, 1)), to: endOfMonth(subMonths(now, 1)) }
    case 'this_year':
      return { from: startOfYear(now), to: now }
    case 'last_year':
      return { from: startOfYear(subYears(now, 1)), to: endOfYear(subYears(now, 1)) }
    default:
      return undefined
  }
}

interface SavedViewsContextType {
  savedViews: SavedView[]
  isLoading: boolean
  addSavedView: (params: CreateViewParams) => Promise<void>
  deleteSavedView: (id: string) => void
  isSidebarOpen: boolean
  setIsSidebarOpen: (open: boolean) => void
  isModalOpen: boolean
  setIsModalOpen: (open: boolean) => void
  currentFilters: SavedViewFilter[]
  setCurrentFilters: (filters: SavedViewFilter[]) => void
  applyView: (view: SavedView) => void
  appliedView: SavedView | null
  clearAppliedView: () => void
  allFiltersState: AllFiltersState | null
  setAllFiltersState: (state: AllFiltersState) => void
}

const SavedViewsContext = createContext<SavedViewsContextType | undefined>(undefined)

// Convert API operation to UI operation label
function getOperationLabel(operation: string): string {
  switch (operation) {
    case 'is':
      return 'is'
    case 'is_not':
      return 'is not'
    default:
      return 'is'
  }
}

// Normalize UI operation to API operation
function normalizeOperation(operation: string): 'is' | 'is_not' {
  return operation === 'is_not' ? 'is_not' : 'is'
}

// Convert API response to UI format
function apiViewToUiView(apiView: SavedViewResponse): SavedView {
  const metadataFilters: SavedViewFilter[] = (apiView.filters.metadata_filters || []).map((meta, index) => {
    return {
      id: `${apiView.id}-filter-${index}`,
      tagName: meta.key,
      tagNameLabel: meta.key,
      operation: meta.operation,
      operationLabel: getOperationLabel(meta.operation),
      value: meta.value,
    }
  })

  return {
    id: apiView.id,
    title: apiView.title,
    description: apiView.description || '',
    relativeDate: apiView.filters.relative_date || null,
    modelSelection: apiView.filters.model_selection || null,
    models: [], // Legacy support - now derived from modelSelection
    metadataFilters,
    granularity: apiView.filters.granularity || null,
    groupBy: apiView.filters.group_by || null,
    createdBy: {
      id: apiView.created_by.id,
      firstName: apiView.created_by.first_name,
      lastName: apiView.created_by.last_name,
      email: apiView.created_by.email,
    },
    createdAt: apiView.created_at,
    lastEditedAt: apiView.updated_at,
  }
}

// Convert UI params to API filters format
function createViewParamsToApiFilters(params: CreateViewParams): ViewFilters {
  const metadataFilters: ViewMetadataFilter[] = params.metadataFilters.map(filter => ({
    key: filter.tagName,
    operation: normalizeOperation(filter.operation),
    value: filter.value,
  }))

  // Only include model_selection if it has meaningful content
  const hasModelSelection = params.modelSelection.all || 
    params.modelSelection.providers.length > 0 || 
    params.modelSelection.models.length > 0

  return {
    relative_date: params.relativeDate,
    model_selection: hasModelSelection ? params.modelSelection : null,
    metadata_filters: metadataFilters.length > 0 ? metadataFilters : null,
    granularity: params.granularity as ViewFilters['granularity'] || null,
    group_by: params.groupBy || null,
  }
}

export function SavedViewsProvider({ children }: { children: React.ReactNode }) {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [currentFilters, setCurrentFilters] = useState<SavedViewFilter[]>([])
  const [allFiltersState, setAllFiltersState] = useState<AllFiltersState | null>(null)
  const [appliedView, setAppliedView] = useState<SavedView | null>(null)

  // API hooks
  const { data: viewsData, isLoading } = useSavedViewsList()
  const createViewMutation = useCreateSavedView()
  const deleteViewMutation = useDeleteSavedView()

  // Convert API views to UI format
  const savedViews: SavedView[] = (viewsData?.items || []).map(apiViewToUiView)

  const addSavedView = useCallback(async (params: CreateViewParams) => {
    const apiFilters = createViewParamsToApiFilters(params)

    const request: CreateSavedViewRequest = {
      title: params.title,
      description: params.description || null,
      filters: apiFilters,
    }

    await createViewMutation.mutateAsync(request)
  }, [createViewMutation])

  const deleteSavedView = useCallback((id: string) => {
    deleteViewMutation.mutate(id)
  }, [deleteViewMutation])

  const applyView = useCallback((view: SavedView) => {
    setCurrentFilters(view.metadataFilters)
    setAppliedView(view)
    setIsSidebarOpen(false)
  }, [])

  const clearAppliedView = useCallback(() => {
    setAppliedView(null)
  }, [])

  const value: SavedViewsContextType = {
    savedViews,
    isLoading,
    addSavedView,
    deleteSavedView,
    isSidebarOpen,
    setIsSidebarOpen,
    isModalOpen,
    setIsModalOpen,
    currentFilters,
    setCurrentFilters,
    applyView,
    appliedView,
    clearAppliedView,
    allFiltersState,
    setAllFiltersState,
  }

  return (
    <SavedViewsContext.Provider value={value}>
      {children}
    </SavedViewsContext.Provider>
  )
}

export function useSavedViews() {
  const context = useContext(SavedViewsContext)
  if (context === undefined) {
    throw new Error("useSavedViews must be used within a SavedViewsProvider")
  }
  return context
}
